// ============================================================
// W'ILY'S PIER — COSMETIC MANIFEST
// Maps app-agnostic cosmetic IDs (from IE_COSMETIC_CATALOG.md)
// to Pier's own sprite assets.
//
// Shape MUST match Terror's and Snake Eyes' manifests — same keys,
// different URLs. Each game renders the same cosmetic in its own
// visual language. Pier's aesthetic: cosmic-boardwalk-over-void-ocean,
// nautical-weathered, salt-spray, lighthouse-lantern glow.
//
// Missing-render fallback: getCosmeticAsset returns null if Pier
// doesn't have a sprite for an ID. Never throws — renderers skip
// the slot silently.
//
// To move Pier's cosmetic CDN later: change PIER_ASSETS_CDN below.
// ============================================================

// Pier uses its own asset CDN, not Terror's. This gives Pier the
// freedom to render cosmetics with nautical-themed variants of the
// same IDs without Terror's manuscript-style sprites leaking in.
const PIER_ASSETS_CDN = "https://pier-assets.pages.dev";

const cosmeticUrl = (filename) => `${PIER_ASSETS_CDN}/cosmetics/${filename}`;

// ── THE MANIFEST ──────────────────────────────────────────────
// All 61 seeded catalog entries. URLs resolve to 404 until Pier
// assets are dropped into the CDN folder — Sprite fallback handles
// that. Safe to ship today.

export const COSMETICS = {
  head: {
    aenarchist_crown:       cosmeticUrl("head_aenarchist_crown.webp"),
    bebling_beanie:         cosmeticUrl("head_bebling_beanie.webp"),
    croupiers_visor:        cosmeticUrl("head_croupiers_visor.webp"),
    dock_knit_cap:          cosmeticUrl("head_dock_knit_cap.webp"),
    gamblers_top_hat:       cosmeticUrl("head_gamblers_top_hat.webp"),
    harbormasters_tricorne: cosmeticUrl("head_harbormasters_tricorne.webp"),
    jynxi_jesters_hood:     cosmeticUrl("head_jynxi_jesters_hood.webp"),
    lyt_brow_circlet:       cosmeticUrl("head_lyt_brow_circlet.webp"),
    nihil_shroud:           cosmeticUrl("head_nihil_shroud.webp"),
    omnis_coronet:          cosmeticUrl("head_omnis_coronet.webp"),
    ruks_broken_helm:       cosmeticUrl("head_ruks_broken_helm.webp"),
    scholars_cowl:          cosmeticUrl("head_scholars_cowl.webp"),
    willies_old_cap:        cosmeticUrl("head_willies_old_cap.webp"),
  },

  face: {
    card_sharp_domino:      cosmeticUrl("face_card_sharp_domino.webp"),
    khao_duhmdumn_paint:    cosmeticUrl("face_khao_duhmdumn_paint.webp"),
    dragons_breath_mask:    cosmeticUrl("face_dragons_breath_mask.webp"),
    fog_veil:               cosmeticUrl("face_fog_veil.webp"),
    h01_visor:              cosmeticUrl("face_h01_visor.webp"),
    jynxi_trickster_mask:   cosmeticUrl("face_jynxi_trickster_mask.webp"),
    salt_spray_goggles:     cosmeticUrl("face_salt_spray_goggles.webp"),
    smoke_mask:             cosmeticUrl("face_smoke_mask.webp"),
    soulcerers_blindfold:   cosmeticUrl("face_soulcerers_blindfold.webp"),
    spectral_tiger_stripes: cosmeticUrl("face_spectral_tiger_stripes.webp"),
  },

  body: {
    anarchists_cloak:        cosmeticUrl("body_anarchists_cloak.webp"),
    apprentices_robe:        cosmeticUrl("body_apprentices_robe.webp"),
    dealers_vest:            cosmeticUrl("body_dealers_vest.webp"),
    dockhands_coat:          cosmeticUrl("body_dockhands_coat.webp"),
    empire_regalia:          cosmeticUrl("body_empire_regalia.webp"),
    gatekeepers_plate:       cosmeticUrl("body_gatekeepers_plate.webp"),
    gilded_gamblers_suit:    cosmeticUrl("body_gilded_gamblers_suit.webp"),
    ink_robe:                cosmeticUrl("body_ink_robe.webp"),
    lighthouse_keepers_coat: cosmeticUrl("body_lighthouse_keepers_coat.webp"),
    omnia_vestments:         cosmeticUrl("body_omnia_vestments.webp"),
    pinstripe_suit:          cosmeticUrl("body_pinstripe_suit.webp"),
    pirate_wrap:             cosmeticUrl("body_pirate_wrap.webp"),
    starter_tunic:           cosmeticUrl("body_starter_tunic.webp"),
  },

  back: {
    companion_harness:     cosmeticUrl("back_companion_harness.webp"),
    dealers_apron:         cosmeticUrl("back_dealers_apron.webp"),
    gambler_cape:          cosmeticUrl("back_gambler_cape.webp"),
    harbormaster_longcoat: cosmeticUrl("back_harbormaster_longcoat.webp"),
    nullus_cape:           cosmeticUrl("back_nullus_cape.webp"),
    omnis_cloak:           cosmeticUrl("back_omnis_cloak.webp"),
    phantom_drift:         cosmeticUrl("back_phantom_drift.webp"),
    shadow_wings:          cosmeticUrl("back_shadow_wings.webp"),
    shipwreck_kit:         cosmeticUrl("back_shipwreck_kit.webp"),
    snake_eyes_banner:     cosmeticUrl("back_snake_eyes_banner.webp"),
    spectral_tail:         cosmeticUrl("back_spectral_tail.webp"),
  },

  aura: {
    crest_well_shimmer: cosmeticUrl("aura_crest_well_shimmer.webp"),
    first_win_glow:     cosmeticUrl("aura_first_win_glow.webp"),
    gold_leaf_sheen:    cosmeticUrl("aura_gold_leaf_sheen.webp"),
    ink_drift:          cosmeticUrl("aura_ink_drift.webp"),
    lantern_glow:       cosmeticUrl("aura_lantern_glow.webp"),
    neon_halo:          cosmeticUrl("aura_neon_halo.webp"),
    prophecy_flicker:   cosmeticUrl("aura_prophecy_flicker.webp"),
    ruk_emberfall:      cosmeticUrl("aura_ruk_emberfall.webp"),
    saltwater_aura:     cosmeticUrl("aura_saltwater_aura.webp"),
  },

  title: {
    // Titles render as text, not sprites. The string is the display text.
    aenarchist:    "Aenarchist",
    apprentice:    "Apprentice",
    bladesworn:    "Bladesworn",
    crest_trader:  "Crest Trader",
    dragon_slayer: "Dragon Slayer",
    harbormaster:  "Harbormaster",
    high_roller:   "High Roller",
    hollowsworn:   "Hollowsworn",
    house_killer:  "House Killer",
    jynxis_jester: "J'aeonxi's Jester",
    lucky_one:     "the Lucky One",
    the_unbroken:  "the Unbroken",
    the_wanderer:  "the Wanderer",
    welcomed:      "the Welcomed",
  },
};

// ── LOOKUP HELPERS ────────────────────────────────────────────

export const getCosmeticAsset = (slot, cosmeticId) => {
  if (!slot || !cosmeticId) return null;
  return COSMETICS[slot]?.[cosmeticId] ?? null;
};

export const resolveEquipped = (equipped) => {
  if (!equipped) return {};
  const out = {};
  for (const [slot, id] of Object.entries(equipped)) {
    out[slot] = getCosmeticAsset(slot, id);
  }
  return out;
};

export const canRender = (slot, cosmeticId) =>
  getCosmeticAsset(slot, cosmeticId) !== null;
